import { MySection } from '@/components/custom/my-section';
import { useCustomers } from '@/services/customer-service';
import { Customer } from '@/types/customer-type';
import { OrderCreateFormValues } from '@/types/order-type';
import { IconButton, Stack, Typography } from '@mui/material';
import { MoreVertical } from 'lucide-react';
import { UseFormReturn } from 'react-hook-form';
import { CustomerAddressInfo } from './customer-section/customer-address-info';
import { CustomerHeader } from './customer-section/customer-header';
import { CustomerOrderHistoryInfo } from './customer-section/customer-order-history-info';
import { CustomerSelectionDropdown } from './customer-section/customer-selection-dropdown';
import { hasOrderHistory } from './customer-section/utils/customer-utils';

interface CustomerSectionProps {
  form: UseFormReturn<OrderCreateFormValues>;
  selectedCustomer: Customer | null;
  watchCustomerId: string;
  onClearCustomer: () => void;
}

export function CustomerSection({
  form,
  selectedCustomer,
  watchCustomerId,
  onClearCustomer,
}: CustomerSectionProps) {
  // Fetch initial customers list for display name lookup
  const { items: allCustomers } = useCustomers({
    page: 0,
    size: 20,
  });

  const handleClear = () => {
    form.setValue('customerId', '');
    onClearCustomer();
  };

  return (
    <MySection
      title="Customer"
      action={
        <IconButton size="small">
          <MoreVertical size={16} />
        </IconButton>
      }
    >
      <Stack spacing={3}>
        {selectedCustomer ? (
          <>
            <CustomerHeader customer={selectedCustomer} onClear={handleClear} />

            {hasOrderHistory(selectedCustomer) && (
              <CustomerOrderHistoryInfo customer={selectedCustomer} />
            )}

            <CustomerAddressInfo customer={selectedCustomer} />
          </>
        ) : (
          <Stack spacing={0.5}>
            <Typography variant="subtitle2">Contact information</Typography>
            <Typography variant="body2" color="text.secondary">
              No customer selected
            </Typography>
          </Stack>
        )}

        <CustomerSelectionDropdown
          form={form}
          selectedCustomer={selectedCustomer}
          watchCustomerId={watchCustomerId}
          allCustomers={allCustomers}
        />
      </Stack>
    </MySection>
  );
}
